import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useDeleteVoucher, useGetCoupons, useGetVouchers, useGetCampaigns, useDeleteCoupon } from "../../queries/campaign";
import { useGetActiveOrganiser } from "../../queries/user";
import { useMemo, useState, useEffect } from "react";
import Chip from "../../components/Chip";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from "../../components/TanStackTable";
import { FaTrash } from "react-icons/fa";
import { ActionButton } from '../../components/Forms/ActionButton';
import { IoIosFlash } from "react-icons/io";
import CouponLogsModal from "../../components/CouponLogsModal";
import { deleteVoucher } from "../../api/campaign";
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const ListCoupons = () => {
    const navigate = useNavigate();

    // Filter states
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        campaign: '',
        sponsor: '',
        status: ''
    });
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [selectedCouponForLogs, setSelectedCouponForLogs] = useState(null);

    // Data fetching
    const { data: couponsData, isPending } = useGetCoupons();
    const { data: campaignsData } = useGetCampaigns();
    const { data: organiserList = [] } = useGetActiveOrganiser();
    const { mutateAsync: deleteCoupon, isLoading: isDeleting } = useDeleteCoupon();

    useEffect(() => {
        if (couponsData) {
            setFilteredData(couponsData);
        }
    }, [couponsData]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = couponsData;

        // Campaign filter
        if (filters.campaign) {
            filtered = filtered.filter((coupon) =>
                coupon.campaignId?._id === filters.campaign
            );
        }

        // Sponsor filter
        if (filters.sponsor) {
            filtered = filtered.filter((coupon) => {
                const sponsorCompanyId = coupon.campaignId?.companyId?._id;
                const sponsorNgoId = coupon.campaignId?.ngoId?._id;
                const sponsorId = sponsorCompanyId || sponsorNgoId;
                return sponsorId === filters.sponsor;
            });
        }

        // Status filter
        if (filters.status) {
            filtered = filtered.filter((coupon) => {
                const userCouponDetails = coupon.userCouponDetails;

                if (filters.status === 'Unassigned') {
                    // Only show coupons with no userCouponDetails or empty array
                    return !userCouponDetails || userCouponDetails.length === 0;
                } else if (filters.status === 'Assigned') {
                    // Only show coupons that have assigned entries but NO redeemed entries
                    if (!userCouponDetails || userCouponDetails.length === 0) return false;

                    const hasRedeemed = userCouponDetails.some(detail => detail.isRedeemed === true);
                    const hasAssigned = userCouponDetails.some(detail => detail.status === 'assigned' && detail.isRedeemed === false);

                    return hasAssigned && !hasRedeemed;
                } else if (filters.status === 'Redeemed') {
                    // Only show coupons that have at least one redeemed entry
                    return userCouponDetails && userCouponDetails.some(detail => detail.isRedeemed === true);
                }
                return true;
            });
        }

        setFilteredData(filtered);
    };

    const handleViewCouponLogs = (coupon) => {
        setSelectedCouponForLogs(coupon);
        setShowLogsModal(true);
    };

    // Prepare campaign options
    const campaignOptions = useMemo(() => {
        if (!campaignsData) return [];
        return campaignsData.map(campaign => ({
            value: campaign._id,
            label: campaign.title
        }));
    }, [campaignsData]);

    // Prepare sponsor options from organiser list
    const sponsorOptions = useMemo(() => {
        if (!organiserList) return [];

        return organiserList.map(organiser => ({
            value: organiser._id,
            label: organiser.companyName || organiser.ngoName || organiser.name || 'Unknown'
        }));
    }, [organiserList]);

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('code', {
            header: 'Coupon Code',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-1">
                    {Array.isArray(info.getValue()) ? info.getValue().join(', ') : (info.getValue() || 'N/A')}
                </div>
            ),
        }),
        // columnHelper.accessor('brand', {
        //     header: 'Brand',
        //     cell: info => (
        //         <div className="whitespace-normal break-words line-clamp-2">
        //             {info.getValue()?.name || info.getValue() || 'N/A'}
        //         </div>
        //     ),
        // }),
        columnHelper.accessor('productId', {
            header: 'Brand/Product',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.row.original.brandId?.brandName || info.getValue()?.name || info.getValue() || 'N/A'}
                    <p className="text-sm text-secondary">
                        {info.row.original.productId?.name ? 'Product' : (info.row.original.brandId?.brandName ? 'Brand' : '')}
                    </p>
                </div>
            ),
        }),
        columnHelper.accessor('campaignId.title', {
            header: 'Campaign',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue()?.title || info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('discountValue', {
            header: 'Coupon Type',
            align: 'center',
            cell: info => {
                const couponType = info.row.original?.campaignId?.couponCodeFormat;
                return (
                    <div className="whitespace-normal break-words text-center capitalize">
                        {couponType || "N/A"}
                    </div>
                );
            },
        }),
        columnHelper.accessor('couponType', {
            header: 'Disc',
            align: 'center',
            cell: info => {
                const typeOfDiscount = info.row.original?.typeOfDiscount;
                const discountValue = info.row.original?.discountValue;
                return (
                    <div className="whitespace-normal break-words text-center">
                        {typeOfDiscount === 'percentage' ? `${discountValue || 0} %` : `${discountValue || 0} Flat`}
                    </div>
                );
            },
        }),
        columnHelper.accessor('sponsor', {
            header: 'Sponsor',
            align: 'center',
            cell: info => {
                const sponsorCompany = info.row.original?.campaignId?.companyId?.companyName;
                const sponsorNgo = info.row.original?.campaignId?.NGOId?.ngoName;
                return (
                    <div className="whitespace-normal break-words text-center flex flex-col gap-1">
                        {sponsorCompany ? <span>{sponsorCompany}</span> : <span>{sponsorNgo || 'N/A'}</span>}
                        {sponsorCompany ? <span className="text-sm text-secondary">Company</span> : <span className="text-sm text-secondary">NGO</span>}
                    </div>
                );
            },
        }),
        columnHelper.accessor('status', {
            header: () => <div className="text-center">Status</div>,
            size: 10,
            align: 'center',
            cell: info => {
                const userCouponDetails = info.row.original?.userCouponDetails;
                const couponType = info.row.original?.campaignId?.couponCodeFormat;

                // Check if unassigned
                if (!userCouponDetails || userCouponDetails.length === 0) {
                    return (
                        <div className="flex flex-col gap-1">
                            {
                                couponType === 'universal' && (
                                    <p className="text-sm text-secondary">Max Redemption Count: {info.row.original?.maxRedemptionCount || 0}</p>
                                )
                            }
                            <div>
                                <Chip
                                    value={'Unassigned'}
                                    color="yellow"
                                />
                            </div>
                        </div>
                    );
                }

                // Check if redeemed (priority over assigned)
                const redeemedCoupon = userCouponDetails.find(detail => detail.isRedeemed === true);
                if (redeemedCoupon) {
                    const totalRedeemed = info.row.original?.total_redeems_count || 0;
                    const maxRedemptions = info.row.original?.maxRedemptionCount || 0;
                    const redemptionText = maxRedemptions === 0 ? `${totalRedeemed}/ Redeemed` : `${totalRedeemed}/${maxRedemptions} Redeemed`;

                    return (
                        <div className="flex flex-col gap-1">
                            {
                                couponType === 'universal' && (
                                    <p className="text-sm text-secondary">Max Redemption Count: {maxRedemptions === 0 ? '∞' : maxRedemptions}</p>
                                )
                            }
                            <div>
                                <Chip
                                    value={couponType === 'universal' ? redemptionText : "Redeemed"}
                                    color="green"
                                />
                            </div>
                            <p className="text-sm text-secondary">
                                {`${couponType === 'universal' ? "Last Redeemed at" : "Redeemed at"}: ${new Date(redeemedCoupon.redeemedAt).toLocaleDateString()} by ${redeemedCoupon.userId?.username || 'Unknown User'}`}
                            </p>
                        </div>
                    );
                }

                // Check if assigned but not redeemed
                const assignedCoupon = userCouponDetails.find(detail => detail.status === 'assigned' && detail.isRedeemed === false);
                if (assignedCoupon) {
                    return (
                        <div className="flex flex-col gap-1">
                            {
                                couponType === 'universal' && (
                                    <p className="text-sm text-secondary">Max Redemption Count: {info.row.original?.maxRedemptionCount || 0}</p>
                                )
                            }
                            <div>
                                <Chip
                                    value={'Assigned'}
                                    color="blue"
                                />
                            </div>
                            <p className="text-sm text-secondary">
                                {`Assigned at: ${new Date(assignedCoupon.createdAt).toLocaleDateString()} to ${assignedCoupon.userId?.username || 'Unknown User'}`}
                            </p>
                        </div>
                    );
                }

                // Default fallback
                return (
                    <Chip
                        value={'Unassigned'}
                        color="yellow"
                    />
                );
            },
        }),
        columnHelper.accessor('validTill', {
            header: 'Valid Till',
            align: 'center',
            cell: info => {
                const validTill = info.row.original?.campaignId?.endDate;
                if (!validTill) return 'N/A';

                const endDate = new Date(validTill);
                return (
                    <div className="whitespace-normal break-words text-center">
                        {endDate.toLocaleDateString()}
                        <p className="text-sm text-secondary">
                            {endDate.toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })}
                        </p>
                    </div>
                );
            },
        }),
        // columnHelper.accessor('assignedTo', {
        //     header: 'Assigned To',
        //     align: 'center',
        //     cell: info => {
        //         const userCouponDetails = info.row.original?.userCouponDetails;
        //         if (userCouponDetails && userCouponDetails.length > 0) {
        //             const assignedCoupon = userCouponDetails.find(detail => detail.status === 'assigned');
        //             console.log(assignedCoupon);
        //             if (assignedCoupon) {
        //                 const userName = `${assignedCoupon.userId?.username || ''}`.trim();
        //                 return (
        //                     <div className="whitespace-normal break-words text-center">
        //                         Assigned to {userName || 'Unknown User'}
        //                     </div>
        //                 );
        //             }
        //         }
        //         return (
        //             <div className="whitespace-normal break-words text-center text-gray-500">
        //                 Not Assigned
        //             </div>
        //         );
        //     },
        // }),
        // columnHelper.accessor('redeemedAt', {
        //     header: 'Redeemed At',
        //     align: 'center',
        //     cell: info => {
        //         const userCouponDetails = info.row.original?.userCouponDetails;
        //         if (userCouponDetails && userCouponDetails.length > 0) {
        //             const redeemedCoupon = userCouponDetails.find(detail => detail.isRedeemed === true);
        //             if (redeemedCoupon && redeemedCoupon.redeemedAt) {
        //                 const redeemedDate = new Date(redeemedCoupon.redeemedAt);
        //                 return (
        //                     <div className="whitespace-normal break-words text-center">
        //                         {redeemedDate.toLocaleDateString('en-US', {
        //                             year: 'numeric',
        //                             month: 'short',
        //                             day: 'numeric',
        //                             hour: '2-digit',
        //                             minute: '2-digit'
        //                         })}
        //                     </div>
        //                 );
        //             }
        //         }
        //         return (
        //             <div className="whitespace-normal break-words text-center text-gray-500">
        //                 -
        //             </div>
        //         );
        //     },
        // }),
        columnHelper.accessor('actions', {
            header: () => <div className="text-center">Actions</div>,
            enableColumnFilter: false,
            cell: info => {
                return (
                    <div className='flex items-center justify-center'>
                        <button
                            className="rounded p-1.5 bg-[rgba(255,193,7,0.1)] hover:bg-[rgba(255,193,7,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleViewCouponLogs(info.row.original)}
                            title="View Coupon Logs"
                        >
                            <IoIosFlash className="fill-orange" />
                        </button>
                        {/* <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => navigate('/coupons/create-coupon', { state: { couponId: info.row.original?._id } })}
                        >
                            <img src={EditIcon} alt="edit" />
                        </button> */}
                        {/* {console.log(info.row.original?.userCouponDetails.find(details => details.status === 'assigned' && details.isRedeemed === false))} */}
                        {
                            info.row.original?.userCouponDetails.length === 0 && (
                                <button
                                    className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                                    disabled={isDeleting}
                                    onClick={() => {
                                        handleDelete(info.row.original._id);
                                    }}
                                >
                                    <FaTrash className="fill-chipRed" />
                                </button>
                            )
                        }
                    </div>
                )
            },
        }),
    ], [navigate]);

    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this coupon?");
        if (!confirm) return;
        await deleteCoupon(id, {
            onSuccess: () => {
                toast.success('Coupon deleted successfully');
            },
            onError: (error) => {
                toast.error(`Error deleting coupon: ${error.message || 'Unknown error'}`);
            },
        });
    }

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of Coupons</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        {/* Campaign Filter */}
                        <div className="flex-col w-full">
                            <label htmlFor="campaign" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Campaign
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="campaign"
                                    value={filters.campaign}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Campaigns</option>
                                    {campaignOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Sponsor Filter */}
                        <div className="flex-col w-full">
                            <label htmlFor="sponsor" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Sponsor
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="sponsor"
                                    value={filters.sponsor}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Sponsors</option>
                                    {sponsorOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Status</option>
                                    <option value="Unassigned">Unassigned</option>
                                    <option value="Assigned">Assigned</option>
                                    <option value="Redeemed">Redeemed</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                campaign: '',
                                sponsor: '',
                                status: ''
                            });
                            setFilteredData(couponsData);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full pl-6">
                    <h1 className="self-start text-lg font-bold pb-4">List of Coupons</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No coupons available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
            
            {/* Coupon Logs Modal */}
            <CouponLogsModal
                isOpen={showLogsModal}
                onClose={() => {
                    setShowLogsModal(false);
                    setSelectedCouponForLogs(null);
                }}
                coupon={selectedCouponForLogs}
            />
        </main>
    );
}

export default ListCoupons